import { useEffect, useState } from 'preact/hooks';
import type { PageProps } from '../app';
import { verifyConsistency, type ConsistencyResult } from '../core/merkle/proof';
import type { ConsistencyProof as Proof, LogClient } from '../core/log/client';
import { navigate } from '../router';
import { ErrorBox } from '../ui/components/ErrorBox';
import { Hash } from '../ui/components/Hash';
import { Icon } from '../ui/components/Icon';
import { LogLayout } from '../ui/components/LogLayout';
import { ConsistencyPath } from '../ui/components/MerklePath';
import { Card, Section } from '../ui/components/Section';
import { fmtInt, fmtTime, parseIndex } from '../ui/format';
import { useLogClient } from '../ui/hooks/useLogClient';
import { resolveRoot, type ResolvedRoot } from '../ui/roots';
import { headHistory, refreshTreeHead, treeHeads } from '../ui/state/treeHeads';
import { LoadingPage, NotFoundLog } from './Log';
import { usePageState } from '../ui/state/pageState';
import { logRoute } from '../ui/nav';
import { ReturnStrip } from '../ui/components/ReturnStrip';
import { ExportMenu } from '../ui/components/Export';

interface Outcome {
  proof: Proof;
  result: ConsistencyResult;
  first: ResolvedRoot;
  second: ResolvedRoot;
}

export function ConsistencyProofPage({ params, query }: PageProps) {
  const { log, client, loading } = useLogClient(params);
  if (loading) return <LoadingPage />;
  if (!log) return <NotFoundLog id={`${params.op}/${params.log}`} />;
  return (
    <LogLayout log={log} {...(client ? { client } : {})} tab="consistency" trail={[{ label: 'Consistency proof' }]}>
      <ReturnStrip query={query} />
      {client && <ConsistencyForm client={client} query={query} />}
    </LogLayout>
  );
}

function ConsistencyForm({ client, query }: { client: LogClient; query: URLSearchParams }) {
  const id = client.log.id;
  const head = treeHeads.value[id];
  const [first, setFirst] = usePageState(`consistency:${id}:first`, query.get('first') ?? '');
  const [second, setSecond] = usePageState(`consistency:${id}:second`, query.get('second') ?? '');
  const [firstRoot, setFirstRoot] = usePageState(`consistency:${id}:firstRoot`, '');
  const [secondRoot, setSecondRoot] = usePageState(`consistency:${id}:secondRoot`, '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = usePageState<unknown>(`consistency:${id}:err`, undefined);
  const [out, setOut] = usePageState<Outcome | undefined>(`consistency:${id}:out`, undefined);
  const [autoRan, setAutoRan] = useState(false);
  const history = headHistory(id).filter((h) => !head || h.treeSize < head.treeSize);

  useEffect(() => {
    void refreshTreeHead(client).catch(() => undefined);
  }, [client]);
  useEffect(() => {
    if (head && !second) setSecond(String(head.treeSize));
    if (head && !first && history.length) setFirst(String(history[history.length - 1]!.treeSize));
  }, [head]);

  const a = parseIndex(first);
  const b = parseIndex(second);
  const canRun = a !== undefined && b !== undefined && a > 0 && a <= b;

  const run = async (e?: Event) => {
    e?.preventDefault();
    if (!canRun || a === undefined || b === undefined) return;
    setBusy(true);
    setErr(undefined);
    setOut(undefined);
    navigate(logRoute(client.log, 'consistency'), { first: a, second: b, from: query.get('from') }, true);
    try {
      const [r1, r2] = await Promise.all([resolveRoot(client, a, head, firstRoot), resolveRoot(client, b, head, secondRoot)]);
      if (!r1) throw new Error(`No root hash is known for tree size ${fmtInt(a)}. Paste one (from an STH/checkpoint you saved) in "Old root".`);
      if (!r2) throw new Error(`No root hash is known for tree size ${fmtInt(b)}. Paste one in "New root" or use the current head size.`);
      const proof = await client.getConsistency(a, b);
      const result = await verifyConsistency(a, b, proof.proof, r1.root, r2.root);
      setOut({ proof, result, first: r1, second: r2 });
    } catch (ex) {
      setErr(ex);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (autoRan || !head || !query.get('first') || !second) return;
    setAutoRan(true);
    void run();
  }, [head, second]);

  return (
    <div class="stack">
      <Card title={<><Icon name="tree" /> Consistency proof</>} foot={<>Proves that the tree at an older size is a prefix of the newer one — the log only appended, never rewrote history. Roots come from the current signed head, from heads this browser saw earlier{client.kind === 'tiled' ? ', or are recomputed from tiles' : ''}; you can also paste them.</>}>
        <form onSubmit={run}>
          <div class="form-grid">
            <label class="field">
              <span>Old tree size</span>
              <input class="input mono" inputMode="numeric" value={first} onInput={(e) => setFirst((e.target as HTMLInputElement).value)} placeholder="e.g. 1000000" autofocus />
              {history.length === 0 && <span class="hint">Heads you fetch over time are remembered here, so you can pick an earlier size later.</span>}
              {history.length > 0 && (
                <select class="input input-sm" value="" onChange={(e) => { const v = (e.target as HTMLSelectElement).value; if (v) setFirst(v); }}>
                  <option value="">Use a head seen earlier…</option>
                  {history.slice().reverse().map((h) => <option key={h.treeSize} value={String(h.treeSize)}>{fmtInt(h.treeSize)} — {fmtTime(h.timestamp ?? h.fetchedAt)}</option>)}
                </select>
              )}
            </label>
            <label class="field">
              <span>New tree size <span class="hint">{head ? `current head: ${fmtInt(head.treeSize)}` : ''}</span></span>
              <input class="input mono" inputMode="numeric" value={second} onInput={(e) => setSecond((e.target as HTMLInputElement).value)} />
            </label>
            <label class="field">
              <span>Old root <span class="hint">optional</span></span>
              <input class="input mono" value={firstRoot} onInput={(e) => setFirstRoot((e.target as HTMLInputElement).value)} placeholder="hex or base64" />
            </label>
            <label class="field">
              <span>New root <span class="hint">optional</span></span>
              <input class="input mono" value={secondRoot} onInput={(e) => setSecondRoot((e.target as HTMLInputElement).value)} placeholder="hex or base64" />
            </label>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" disabled={!canRun || busy}><Icon name="play" /> {busy ? 'Verifying…' : 'Fetch & verify'}</button>
            {a !== undefined && b !== undefined && a > b && <span class="small muted">the old size must not exceed the new size</span>}
          </div>
        </form>
      </Card>

      {err !== undefined && <ErrorBox error={err} title="Could not verify consistency" />}
      {out && (
        <Card
          title={<>{out.result.ok ? <span class="verdict ok"><Icon name="check" /> Consistent</span> : <span class="verdict err"><Icon name="x" /> Not consistent</span>}</>}
          actions={(() => {
            const json = JSON.stringify({ log: client.log.description, log_id: client.log.logId?.toBase64(), first: out.proof.first, second: out.proof.second, first_root: out.first.root.toBase64(), second_root: out.second.root.toBase64(), consistency: out.proof.proof.map((p) => p.toBase64()), verified: out.result.ok }, null, 2);
            return <ExportMenu label="Proof" icon="tree" formats={[{ label: 'Consistency proof (JSON)', filename: `consistency-${out.proof.first}-${out.proof.second}.json`, mime: 'application/json', text: json }]} />;
          })()}
        >
          {out.result.error && <div class="errorbox" style="margin-bottom:0.8rem">{out.result.error}</div>}
          <dl class="kv">
            <dt>Old tree</dt>
            <dd class="row">{fmtInt(out.proof.first)} entries <Hash value={out.first.root} short /><span class="muted small">{out.first.source}</span></dd>
            <dt>New tree</dt>
            <dd class="row">{fmtInt(out.proof.second)} entries <Hash value={out.second.root} short /><span class="muted small">{out.second.source}</span></dd>
            <dt>Proof</dt>
            <dd>{out.proof.proof.length} hashes{client.kind === 'tiled' ? ' (computed from tiles)' : ' (from the log)'}</dd>
          </dl>
          <div style="margin-top:1rem">
            <Section title="Hash path" count={out.result.steps.length} open={out.result.steps.length <= 12}>
              <ConsistencyPath
                steps={out.result.steps}
                {...(out.proof.proof.length || out.result.firstRootPrepended ? { seed: out.result.firstRootPrepended ? out.first.root : out.proof.proof[0]! } : {})}
                {...(out.result.computedFirstRoot ? { computedFirst: out.result.computedFirstRoot } : {})}
                {...(out.result.computedSecondRoot ? { computedSecond: out.result.computedSecondRoot } : {})}
                firstRoot={out.first.root}
                secondRoot={out.second.root}
                ok={out.result.ok}
                firstRootPrepended={out.result.firstRootPrepended}
              />
            </Section>
          </div>
        </Card>
      )}
    </div>
  );
}
